import {lazy, Suspense, useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {useParams} from 'react-router-dom';
import {useForm} from 'react-hook-form';
import {useBookDeal} from 'hooks';
import {DEVICE, useScreenResize} from '@chekin/guestapp-ui-kit';
import {PATTERNS} from '@chekin/core';
import Page from 'components/Page';
import Input from 'components/common/Input';
import {MobileActionPanel, ConfirmForm, ConfirmButton} from './styled';

const OfferView = lazy(() =>
  import('pages/OfferView/OfferView').then(m => ({default: m.OfferView})),
);

type FormTypes = {
  email: string;
};
function EnterContactEmailView() {
  const {id: offerId} = useParams();
  const {t} = useTranslation();
  const {isMatch: isMobileMode, isInitialized} = useScreenResize(DEVICE.laptop);
  const [hasSubmittedDeal, setHasSubmittedDeal] = useState(false);

  const {
    createDeal,
    offer,
    isLoading,
    bookExperienceEmail,
    navigateAfterSuccess,
    navigateToBookExperience,
    itemQuantities,
  } = useBookDeal({
    offerId,
    onSuccess: () => navigateAfterSuccess(),
  });

  useEffect(() => {
    const hasQuantity = Object.values(itemQuantities).some(q => q > 0);
    if (!hasQuantity && !hasSubmittedDeal) {
      navigateToBookExperience({replace: true});
    }
  }, [itemQuantities, hasSubmittedDeal, navigateToBookExperience]);

  const {register, handleSubmit, formState, watch} = useForm<FormTypes>({
    defaultValues: {
      email: bookExperienceEmail,
    },
  });
  const currentEmail = watch('email');

  const onSubmit = ({email}: FormTypes) => {
    setHasSubmittedDeal(true);
    createDeal(email);
  };

  if (!isInitialized) {
    return null;
  }

  return isMobileMode ? (
    <Page
      isLoading={isLoading}
      isSubHeaderVisible
      title={t('book_experience_view.subtitle')}
      navBarTitle={t('book_experience_view.nav_bar_title')}
      bottomStickyPanel={
        isMobileMode &&
        offer && (
          <MobileActionPanel>
            <ConfirmButton
              disabled={!currentEmail}
              label={t('confirm')}
              onClick={handleSubmit(onSubmit)}
            />
          </MobileActionPanel>
        )
      }
    >
      <ConfirmForm onSubmit={handleSubmit(onSubmit)}>
        <div>{t('book_experience_confirm_email.info')}</div>
        <Input
          {...register('email', {
            required: t('required'),
            pattern: {
              value: PATTERNS.email,
              message: t('invalid_email'),
            },
          })}
          label={t('email')}
          inputMode="email"
          autoCorrect="off"
          spellCheck={false}
          autoCapitalize="none"
          error={formState.errors['email']?.message}
        />
      </ConfirmForm>
    </Page>
  ) : (
    <Suspense fallback={null}>
      <OfferView />
    </Suspense>
  );
}

export {EnterContactEmailView};
